// Copyright 2025, University of Maryland, All Rights Reserved
import { ColumnDef } from "@tanstack/react-table"
import { formatToEastern } from "../../utils/date"
import { formatJobType } from "../../utils/formatJobType"

// Helper function to format metadata values for display
const formatMetadataValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "-"
  }
  
  if (typeof value === 'string') {
    return value
  }
  
  if (typeof value === 'number') {
    return value.toString()
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  
  if (Array.isArray(value)) {
    return value.map(String).join(', ')
  }
  
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  
  return String(value)
}

// Function to create metadata columns
const createMetadataColumns = (metadataFields: Record<string, string>): ColumnDef<Job>[] => {
  return Object.entries(metadataFields).map(([fieldId, label]) => ({
    accessorKey: `metadata.${fieldId}`,
    header: label,
    cell: ({ row }) => {
      const value = row.original.metadata?.[fieldId]
      return (
        <span className="text-sm">
          {formatMetadataValue(value)}
        </span>
      )
    },
    enableColumnFilter: true,
  }))
}


export type Job = {
  job_id: string
  process_name: string
  status: string
  created_at: string
  started_at?: string
  run_duration_ms?: number // completed_at - started_at
  wait_duration_ms?: number // started_at - created_at
  user_id: string
  org_id?: string
  ws_id?: string
  metadata?: Record<string, unknown>
}

// Base columns that are always present
const baseColumns: ColumnDef<Job>[] = [
  {
    accessorKey: "job_id",
    header: "Job ID",
    cell: ({ row }) => (
      <span className="font-mono text-[11.5px] text-slate-600">{row.original.job_id}</span>
    ),
    enableColumnFilter: true,
  },
  {
    accessorKey: "process_name",
    header: "Type",
    // We'll transform process_name -> readable type
    cell: ({ row }) => formatJobType(row.original.process_name),
    // TanStack Table will do text sorting by default
    filterFn: (row, id, value) => {
      const processName = row.getValue(id) as string
      const displayName = formatJobType(processName)
      return displayName.toLowerCase().includes(value.toLowerCase())
    },
  },
  {
    id: "pipeline_id",
    header: "Pipeline",
    cell: ({ row }) => {
      const pipelineId = row.original.metadata?.pipeline_id
      if (!pipelineId) return <span className="text-slate-300">—</span>
      return (
        <span className="font-mono text-[11.5px] text-slate-600">{formatMetadataValue(pipelineId)}</span>
      )
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status
      const chip = {
        "PENDING": "bg-[#fffbeb] text-[#b45309]",
        "RUNNING": "bg-[#eff6ff] text-[#2563eb]",
        "COMPLETED": "bg-[#ecfdf5] text-[#059669]",
        "ERROR": "bg-[#fef2f2] text-[#dc2626]"
      }[status] || "bg-slate-100 text-slate-600"

      return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10.5px] font-[650] uppercase tracking-[0.04em] ${chip}`}>
          {status}
        </span>
      )
    },
  },
  {
    accessorKey: "created_at",
    header: "Created (ET)",
    cell: ({ row }) => formatToEastern(row.original.created_at),
  },
  {
    accessorKey: "wait_duration_ms",
    header: "Wait Time",
    cell: ({ row }) => {
      const duration = row.original.wait_duration_ms
      if (!duration) return <span className="text-slate-300">—</span>
      const isLong = duration > 300000 // 5 mins
      return (
        <span className={`tabular-nums ${isLong ? 'font-semibold text-[#b45309]' : ''}`}>
          {(duration / 1000).toFixed(1)}s
        </span>
      )
    },
  },
  {
    accessorKey: "run_duration_ms",
    header: "Run Time",
    cell: ({ row }) => {
      const duration = row.original.run_duration_ms
      if (!duration) return <span className="text-slate-300">—</span>
      const isLong = duration > 600000 // 10 mins
      return (
        <span className={`tabular-nums ${isLong ? 'font-semibold text-[#dc2626]' : ''}`}>
          {(duration / 1000).toFixed(1)}s
        </span>
      )
    },
  },
  {
    accessorKey: "user_id",
    header: "User",
  },
]

// Function to create context-aware columns for ws_id and org_id
const createContextColumns = (showOrgId: boolean, showWsId: boolean): ColumnDef<Job>[] => {
  const contextColumns: ColumnDef<Job>[] = []
  
  if (showOrgId) {
    contextColumns.push({
      accessorKey: "org_id",
      header: "Org ID",
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.org_id || "-"}
        </span>
      ),
      enableColumnFilter: true,
    })
  }
  
  if (showWsId) {
    contextColumns.push({
      accessorKey: "ws_id",
      header: "WS ID",
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.ws_id || "-"}
        </span>
      ),
      enableColumnFilter: true,
    })
  }
  
  return contextColumns
}

// Function to create job columns with optional metadata fields and context
export const createJobColumns = (
  metadataFields?: Record<string, string>,
  context?: { org_id?: string; ws_id?: string }
): ColumnDef<Job>[] => {
  const columns = [...baseColumns]
  
  // Determine which context columns to show based on the current context
  const showOrgId = !context?.org_id // Show org_id column if we're not in org context
  const showWsId = !context?.ws_id   // Show ws_id column if we're not in workspace context
  
  // Add context columns if needed
  if (showOrgId || showWsId) {
    const contextColumns = createContextColumns(showOrgId, showWsId)
    // Insert context columns before the user column (at index -1)
    columns.splice(-1, 0, ...contextColumns)
  }
  
  // Add metadata columns if provided
  if (metadataFields && Object.keys(metadataFields).length > 0) {
    const metadataColumns = createMetadataColumns(metadataFields)
    // Insert metadata columns before the user column (at index -1)
    columns.splice(-1, 0, ...metadataColumns)
  }
  
  return columns
}
